import React, { useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Node,
  Edge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Paper, Typography, Box } from '@mui/material';
import { AccountTree as TreeIcon } from '@mui/icons-material';
import { DiagramData } from '../../../services/workflow';

interface AgentGraphProps {
  diagramData?: DiagramData | null;
  orchestrationPattern?: string;
}

// Color palette for different node types
const nodeColors: Record<string, { bg: string; border: string }> = {
  root: { bg: '#e3f2fd', border: '#1976d2' },
  coordinator: { bg: '#e3f2fd', border: '#1976d2' },
  specialist: { bg: '#e8f5e9', border: '#4caf50' },
  worker: { bg: '#fff3e0', border: '#ff9800' },
  default: { bg: '#f5f5f5', border: '#9e9e9e' },
};

const AgentGraph: React.FC<AgentGraphProps> = ({
  diagramData,
  orchestrationPattern,
}) => {
  // Convert diagram data to ReactFlow nodes and edges
  const { nodes, edges } = useMemo(() => {
    if (!diagramData || !diagramData.nodes || diagramData.nodes.length === 0) {
      return { nodes: [], edges: [] };
    }

    // Calculate positions based on node count and pattern
    const nodeCount = diagramData.nodes.length;
    const centerX = 250;
    const startY = 50;
    const ySpacing = 120;
    const xSpacing = 180;

    const flowNodes: Node[] = diagramData.nodes.map((node, index) => {
      // Determine position based on orchestration pattern
      let x = centerX;
      let y = startY + index * ySpacing;

      if (orchestrationPattern === 'hierarchical' && nodeCount > 1) {
        // Root at top, others spread below
        if (index === 0) {
          x = centerX;
          y = startY;
        } else {
          const remainingNodes = nodeCount - 1;
          const rowIndex = index - 1;
          const nodesInRow = Math.min(3, remainingNodes);
          const startX = centerX - ((nodesInRow - 1) * xSpacing) / 2;
          x = startX + (rowIndex % nodesInRow) * xSpacing;
          y = startY + ySpacing + Math.floor(rowIndex / nodesInRow) * ySpacing;
        }
      } else if (orchestrationPattern === 'sequential') {
        // Vertical chain
        x = centerX;
        y = startY + index * ySpacing;
      } else if (orchestrationPattern === 'parallel') {
        // Horizontal spread
        const startX = centerX - ((nodeCount - 1) * xSpacing) / 2;
        x = startX + index * xSpacing;
        y = startY + 100;
      }

      const colors = nodeColors[node.type] || nodeColors.default;

      return {
        id: node.id,
        type:
          index === 0
            ? 'input'
            : index === nodeCount - 1 && orchestrationPattern === 'sequential'
              ? 'output'
              : 'default',
        data: { label: node.label },
        position: { x, y },
        style: {
          background: colors.bg,
          border: `2px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '10px 16px',
          minWidth: 140,
          textAlign: 'center' as const,
          fontWeight: 500,
        },
      };
    });

    const flowEdges: Edge[] = diagramData.edges.map((edge, index) => ({
      id: `e${index}`,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      animated: true,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2 },
      labelStyle: { fontSize: 10, fill: '#666' },
    }));

    return { nodes: flowNodes, edges: flowEdges };
  }, [diagramData, orchestrationPattern]);

  // Empty state
  if (!diagramData || nodes.length === 0) {
    return (
      <Paper
        sx={{
          height: 300,
          width: '100%',
          border: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <TreeIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Agent diagram will appear here
        </Typography>
        <Typography variant="caption" color="text.secondary">
          after architecture analysis
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: 350, width: '100%', border: '1px solid #e0e0e0' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Agent Topology
        </Typography>
      </Box>
      <Box sx={{ height: 'calc(100% - 40px)' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          preventScrolling={false}
        >
          <Background color="#e0e0e0" gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </Box>
    </Paper>
  );
};

export default AgentGraph;
